import React, { useState, useRef, Component } from 'react';
import { useRaceStore } from '../../stores/raceStore';
import { Button } from '../ui/Button';
import { parseGpx } from '../../utils/gpxParser';
import { useTranslation, LOCALE_OPTIONS } from '../../utils/i18n';
import { UploadIcon, MapPinIcon, ZapIcon, ClockIcon, FileJsonIcon, UserIcon, HelpCircleIcon } from 'lucide-react';
import { HelpPanel } from '../help/HelpPanel';
import { trackGpxUploaded, trackDemoLoaded, trackPlanImported } from '../../utils/analytics';
export function GpxImportScreen() {
  const {
    t,
    locale,
    setLocale
  } = useTranslation();
  const {
    setGpxData,
    loadSampleData,
    importRacePlan
  } = useRaceStore();
  const [importStatus, setImportStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [langOpen, setLangOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const jsonInputRef = useRef<HTMLInputElement>(null);
  const currentLocale = LOCALE_OPTIONS.find(l => l.value === locale)!;
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result;
      if (typeof text === 'string') {
        const parsed = parseGpx(text);
        setGpxData(parsed);
        trackGpxUploaded({
          distance_km: parsed.totalDistance,
          elevation_gain_m: Math.round(parsed.totalElevationGain),
          elevation_loss_m: Math.round(parsed.totalElevationLoss),
          waypoint_count: parsed.waypoints?.length ?? 0
        });
      }
    };
    reader.readAsText(file);
  };
  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    if (file.name.endsWith('.json')) {
      const reader = new FileReader();
      reader.onload = e => {
        const text = e.target?.result;
        if (typeof text === 'string') {
          const result = importRacePlan(text);
          if (!result.success) {
            setImportStatus({
              type: 'error',
              message: result.message
            });
            setTimeout(() => setImportStatus(null), 5000);
          }
        }
      };
      reader.readAsText(file);
    } else if (file.name.endsWith('.gpx')) {
      const reader = new FileReader();
      reader.onload = e => {
        const text = e.target?.result;
        if (typeof text === 'string') setGpxData(parseGpx(text));
      };
      reader.readAsText(file);
    }
  };
  const handleJsonImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result;
      if (typeof text === 'string') {
        const result = importRacePlan(text);
        trackPlanImported(result.success);
        if (!result.success) {
          setImportStatus({
            type: 'error',
            message: result.message
          });
          setTimeout(() => setImportStatus(null), 5000);
        }
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };
  return <div className="min-h-screen bg-[#F7F7FA] flex flex-col" data-id="element-297">
      <header className="p-6 lg:p-8 flex items-center justify-between" data-id="element-298">
        <div className="flex items-center gap-2" data-id="element-299">
          <div className="w-8 h-8 bg-[#FC4C02] rounded-lg flex items-center justify-center" data-id="element-300">
            <ZapIcon className="h-4 w-4 text-white" data-id="element-301" />
          </div>
          <span className="text-lg font-bold text-[#2D2D2D]" data-id="element-302">
            Trail Race Planner
          </span>
        </div>
        {/* Language Switcher + Help */}
        <div className="flex items-center gap-1" data-id="element-303">
          <div className="relative" data-id="element-304">
            <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 text-[#6B6B76] hover:text-[#2D2D2D] px-2.5 py-1.5 rounded-lg hover:bg-white transition-colors text-sm border border-transparent hover:border-[#E8E8ED]" data-id="element-305">
              <currentLocale.FlagComponent className="w-5 h-3.5 rounded-[2px]" data-id="element-306" />
              <span className="text-xs font-medium" data-id="element-307">
                {currentLocale.value.toUpperCase()}
              </span>
            </button>
            {langOpen && <>
                <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} data-id="element-308" />
                <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-[#E8E8ED] rounded-xl shadow-lg py-1 min-w-[140px]" data-id="element-309">
                  {LOCALE_OPTIONS.map(opt => <button key={opt.value} onClick={() => {
                setLocale(opt.value);
                setLangOpen(false);
              }} className={`w-full text-left px-3 py-2 flex items-center gap-2.5 text-sm hover:bg-[#F7F7FA] transition-colors ${locale === opt.value ? 'font-semibold text-[#FC4C02]' : 'text-[#2D2D2D]'}`} data-id="element-310">
                      <opt.FlagComponent className="w-5 h-3.5 rounded-[2px]" data-id="element-311" />
                      {opt.label}
                    </button>)}
                </div>
              </>}
          </div>
          <button onClick={() => setIsHelpOpen(true)} title={t('header.help')} className="text-[#9B9BA7] hover:text-[#FC4C02] p-2 rounded-lg hover:bg-white transition-colors" data-id="element-312">
            <HelpCircleIcon className="h-5 w-5" data-id="element-313" />
          </button>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center p-6 lg:p-8" data-id="element-314">
        <div className="w-full max-w-lg space-y-8" data-id="element-315">
          <div className="text-center space-y-4" data-id="element-316">
            <h1 className="text-4xl lg:text-5xl font-bold text-[#2D2D2D] leading-tight" data-id="element-317">
              {t('import.title1')}
              <br data-id="element-318" />
              <span className="text-[#9B9BA7]" data-id="element-319">{t('import.title2')}</span>
            </h1>
            <p className="text-[#6B6B76] text-lg" data-id="element-320">{t('import.subtitle')}</p>
          </div>

          {importStatus && <div className={`px-4 py-3 rounded-xl text-sm font-semibold text-center ${importStatus.type === 'success' ? 'bg-[#ECFDF5] text-[#00B843]' : 'bg-[#FEF2F2] text-[#E34402]'}`} data-id="element-321">
              {importStatus.message}
            </div>}

          <div onDrop={handleDrop} onDragOver={e => e.preventDefault()} className="bg-white border-2 border-dashed border-[#E8E8ED] rounded-3xl p-8 lg:p-12 text-center hover:border-[#FC4C02]/50 hover:bg-[#FC4C02]/[0.02] transition-all duration-300 cursor-pointer group" data-id="element-322">
            <label className="cursor-pointer block" data-id="element-323">
              <div className="w-16 h-16 bg-[#FC4C02]/10 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-[#FC4C02]/15 transition-colors" data-id="element-324">
                <UploadIcon className="h-7 w-7 text-[#FC4C02]" data-id="element-325" />
              </div>
              <h2 className="text-xl font-semibold text-[#2D2D2D] mb-2" data-id="element-326">
                {t('import.dropTitle')}
              </h2>
              <p className="text-[#6B6B76] mb-6" data-id="element-327">{t('import.dropSubtitle')}</p>
              <Button variant="primary" as="span" size="lg" className="w-full max-w-xs" data-id="element-328">
                {t('import.selectGpx')}
              </Button>
              <input type="file" accept=".gpx" className="hidden" onChange={handleFileUpload} data-id="element-329" />
            </label>
          </div>

          <div className="flex items-center gap-4" data-id="element-330">
            <div className="flex-1 h-px bg-[#E8E8ED]" data-id="element-331" />
            <span className="text-xs font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-332">
              {t('import.or')}
            </span>
            <div className="flex-1 h-px bg-[#E8E8ED]" data-id="element-333" />
          </div>

          <button onClick={() => jsonInputRef.current?.click()} className="w-full bg-white border border-[#E8E8ED] rounded-2xl p-5 flex items-center gap-4 hover:border-[#FC4C02]/40 hover:bg-[#FC4C02]/[0.02] transition-all group" data-id="element-334">
            <div className="w-12 h-12 bg-[#2D2D2D]/5 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#FC4C02]/10 transition-colors" data-id="element-335">
              <FileJsonIcon className="h-5 w-5 text-[#6B6B76] group-hover:text-[#FC4C02] transition-colors" data-id="element-336" />
            </div>
            <div className="text-left flex-1" data-id="element-337">
              <div className="text-sm font-semibold text-[#2D2D2D]" data-id="element-338">
                {t('import.racePlan')}
              </div>
              <div className="text-xs text-[#9B9BA7] mt-0.5" data-id="element-339">
                {t('import.racePlanDesc')}
              </div>
            </div>
          </button>
          <input ref={jsonInputRef} type="file" accept=".json" onChange={handleJsonImport} className="hidden" data-id="element-340" />

          <div className="text-center" data-id="element-341">
            <button onClick={() => {
            loadSampleData();
            trackDemoLoaded();
          }} className="text-[#FC4C02] hover:text-[#E04400] font-medium text-sm transition-colors" data-id="element-342">
              {t('import.demo')}
            </button>
          </div>

          <div className="grid grid-cols-3 gap-4 pt-8" data-id="element-343">
            {[{
            icon: UserIcon,
            label: 'Profile'
          }, {
            icon: ClockIcon,
            label: t('import.features.pacing')
          }, {
            icon: ZapIcon,
            label: t('import.features.strategy')
          }].map((feature, i) => <div key={i} className="text-center" data-id="element-344">
                <div className="w-12 h-12 bg-white border border-[#E8E8ED] rounded-xl flex items-center justify-center mx-auto mb-3 shadow-sm" data-id="element-345">
                  <feature.icon className="h-5 w-5 text-[#6B6B76]" data-id="element-346" />
                </div>
                <p className="text-sm font-medium text-[#6B6B76]" data-id="element-347">
                  {feature.label}
                </p>
              </div>)}
          </div>
        </div>
      </main>

      <footer className="p-6 text-center" data-id="element-348">
        <p className="text-xs text-[#9B9BA7]" data-id="element-349">{t('import.footer')}</p>
      </footer>

      <HelpPanel isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} data-id="element-350" />
    </div>;
}